import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { Vue } from 'vue-property-decorator';
import axios, { AxiosResponse } from 'axios';
import { API_URL, RES_URL } from '@/App.settings';
import Dictionary from '@/types/Dictionary';

/** Store Vuex de persistence des données liées à la session */
@Module({
	namespaced: true
})
class SessionStore extends VuexModule {
	private logoFile: string = "";
	private currentClientName: string = "";
	private currentClientId: number = 0;
	private isSupervisor: boolean = false;
	private profileId: number = 0;
	private language: string = "";
	private graphTheme: string = "light";
	private isLoggedIn: boolean = false;
	private hasRestrictedPerimeter = false;

	/** Timestamp de la dernière synchro V3 -> V2 */
	private lastSync: number = undefined;

	/** 
	 * Réinitialisation du store
	 * @description A compléter à chaque ajout d'une propriété dans le store... en attendant une meilleure solution 😥
	 */
	@Mutation
	private reset() {
		this.logoFile = "";
		this.currentClientName = "";
		this.currentClientId = 0;
		this.isSupervisor = false;
		this.profileId = 0;
		this.isLoggedIn = false;
		this.lastSync = undefined;
		this.hasRestrictedPerimeter = false;
	}

	/** Défini l'utilisateur courant est loggé ou non */
	@Mutation
	private setIsLoggedIn(value: boolean) {
		this.isLoggedIn = value;
	}

	/** Défini le nom du client actuellement connecté */
	@Mutation
	private setCurrentClientName(newName: string) {
		this.currentClientName = newName;
	}

	@Mutation
	private setCurrentClientId(value: number){
		this.currentClientId = value;
	}

	/** Défini le logo du client */
	@Mutation
	private setCurrentClientLogo(newLogo: string) {
		this.logoFile = newLogo;
	}

	/** Défini si l'utilisateur courant est superviseur ou non */
	@Mutation
	private setUserIsSupervisor(value : boolean){
		this.isSupervisor = value;
	}

	/** Défini le profil de l'utilisateur courant */
	@Mutation
	private setProfileId(value: number){
		this.profileId = value
	}

	/** Défini la langue de l'utilisateur */
	@Mutation
	private setLanguage(value : string){
		this.language = value;
	}

	/** Défini le thème du graphique */
	@Mutation
	private setGraphTheme(value : string){
		this.graphTheme = value;
	}

	/** Défini la date de la dernière synchro v3 -> v2 */
	@Mutation
	private setLastSync(value : number){
		this.lastSync = value;
	}

	/** Défini si l'utilisateur courant possède un périmètre restreint ou non */
	@Mutation
	private setHasRestrictedPerimeter(value: boolean){
		this.hasRestrictedPerimeter = value;
	}

	/** Défini l'utilisateur courant est loggé ou non */
	@Action({ rawError : true})
	public SetIsLoggin(value: boolean) {
		this.context.commit('setIsLoggedIn', value);
	}

    /** Réinitialisation du store Vuex FilterTree */
    @Action({ rawError: true })
    public Reset() {
        // (this as unknown as StoreExtended<any>).reset();
        this.context.commit("reset");
    }

	/** Défini si l'utilisateur courant est superviseur ou non */
	@Action({ rawError : true})
	public SetIsSupervisor(value: boolean) {
		this.context.commit('setUserIsSupervisor', value);
	}

	/** Chargement des données associées à une session */
	@Action({ rawError : true})
	public async Load(){
		const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

		await axios.post(API_URL + "Connexion/loadSessionData", args).then((response) => {
			if (response && response.data) {
				console.debug("SessionStore - Load - response.data", response.data)

				this.context.commit('setCurrentClientName', response.data.Client.Name);
				this.context.commit('setCurrentClientId', response.data.Client.Id);
				if (response.data.LogoFile)
					this.context.commit('setCurrentClientLogo', RES_URL + response.data.LogoFile);
				this.context.commit('setUserIsSupervisor', response.data.IsSupervisor);
				this.context.commit('setProfileId', response.data.ProfileId);
				this.context.commit('setLanguage', response.data.Language);
				this.context.commit('setGraphTheme', response.data.GraphTheme);
				this.context.commit('setHasRestrictedPerimeter', response.data.HasRestrictedPerimeter);
			}
		});
	}

	/** Rafraichi la synchronisation */
	@Action({ rawError : true})
	public async RefreshSync(): Promise<string> {
		const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

		const response: AxiosResponse<any, any> = await axios.post(API_URL + "Connexion/RefreshInteroptKey", args)
		if(response && response.data) {
			this.context.commit('setLastSync', Date.now());
			return response.data;
		}
		else 
			return null;
	}

	/** Initialise le timestamp de synchronisation */
	@Action({ rawError : true})
	public async SetSync(){
		this.context.commit('setLastSync', Date.now());
	}

	/** Nom du client actuellement connnecté */
	public get GetCurrentClientName(): string {
		return this.currentClientName;
	}

	/** Identifiant du client actuellement connnecté */
	public get GetCurrentClientId(): number {
		return this.currentClientId;
	}

	/** Logo du client actuellement connecté */
	public get GetCurrentClientLogo(): string {
		return this.logoFile;
	}

	/** Indique si l'utilisateur courant est superviseur ou non */
	public get GetUserIsSupervisor(){
		return this.isSupervisor;
	}

	public get GetProfileId(){
		return this.profileId;
	}

	public get GetLastSync(){
		return this.lastSync;
	}

	/** Indique la langue de l'utilisateur */
	public get GetLanguage(){
		return this.language;
	}

	/** Indique le thème du graphique */
	public get GetGraphTheme(){
		return this.graphTheme;
	}

	/** Indique si l'utilisateur courant est loggé ou non */
	public get IsLoggedIn(): boolean {
		return this.isLoggedIn;
	}

	/** Indique si l'utilisateur courant possède un périmètre restreint ou non */
	public get GetHasRestrictedPerimeter(): boolean{
		return this.hasRestrictedPerimeter;
	}
};

export default SessionStore;